import { useHistory, useParams } from "react-router-dom";
import { useVendor } from "./hooks/useVendor";
import VendorsLoader from "./components/skeletons/VendorsSkeleton";
import ErrorPage from "../shared/components/BaseError";
import EntityPageLayout from "../shared/components/EntityPageLayout";
import { useIntl } from "react-intl";
import BaseButton from "components/common/BaseButton";
import useMutateVendor from "./hooks/useMutateVendor";
import { useState } from "react";
import DeletionModal from "../shared/components/DeletionModal";

const ViewVendorPage = () => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedEntityId, setSelectedEntityId] = useState<string | null>(null);
  const [entityName, setEntityName] = useState("");

  const history = useHistory();

  const intl = useIntl();

  const { entityId } = useParams<{
    entityId: string;
  }>();

  const {
    data: vendor,
    isLoading,
    isError,
    error,
    refetch,
  } = useVendor({ vendorId: entityId });

    const {
      deleteVendor,
      deleteVendorLoading,
      deleteVendorSuccess,
      deleteVendorReset,
      deleteVendorIsError,
    } = useMutateVendor({ id: selectedEntityId as string });
  
    const handleDelete = () => {
      const vendorName = vendor?.name || "";
      setSelectedEntityId(vendor?.id.toString() || "");
      setShowDeleteModal(true);
      setEntityName(vendorName);
    };

    const onEditClick = () => {
      history.push(`/us-admin-app/vendors/${entityId}/edit`);
    }
  
    const confirmDelete = () => {
      if (selectedEntityId) {
        deleteVendor({ vendorId: selectedEntityId });
      }
    };

  if (isError)
    return (
      <EntityPageLayout headerMessage="usAdmin.viewVendor">
        <ErrorPage errorMessage={error} onRetry={refetch} />
      </EntityPageLayout>
    );

  if (isLoading)
    return (
      <EntityPageLayout headerMessage="usAdmin.viewVendor">
        <VendorsLoader />
      </EntityPageLayout>
    );

  return (
    <EntityPageLayout headerMessage="usAdmin.viewVendor">
      <div className="view-grid">
        <div className="view-container">
          <span className="view-container__title">{intl.formatMessage({ id: "usAdmin.name" })}</span>
          <span>{vendor?.name || "N/A"}</span>
        </div>
        <div className="view-container">
          <span className="view-container__title">{intl.formatMessage({ id: "usAdmin.contactName.vendor" })}</span>
          <span>{vendor?.contactName || "N/A"}</span>
        </div>
        <div className="view-container">
          <span className="view-container__title">{intl.formatMessage({ id: "usAdmin.address" })}</span>
          <span>{vendor?.address || "N/A"}</span>
        </div>
        <div className="view-container">
          <span className="view-container__title">{intl.formatMessage({ id: "usAdmin.email" })}</span>
          <span>{vendor?.email || "N/A"}</span>
        </div>
        <div className="view-container">
          <span className="view-container__title">{intl.formatMessage({ id: "usAdmin.phone" })}</span>
          <span>{vendor?.phone || "N/A"}</span>
        </div>
        <div className="view-container">
          <span className="view-container__title">{intl.formatMessage({ id: "usAdmin.city" })}</span>
          <span>{vendor?.city || "N/A"}</span>
        </div>
        <div className="view-container">
          <span className="view-container__title">{intl.formatMessage({ id: "usAdmin.state" })}</span>
          <span>{vendor?.state || "N/A"}</span>
        </div>
        <div className="view-container">
          <span className="view-container__title">{intl.formatMessage({ id: "country" })}</span>
          <span>{vendor?.country?.name || "N/A"}</span>
        </div>
        <div className="view-container">
          <span className="view-container__title">{intl.formatMessage({ id: "usAdmin.zip" })}</span>
          <span>{vendor?.zip || "N/A"}</span>
        </div>
        <div className="view-container">
          <span className="view-container__title">{intl.formatMessage({ id: "usAdmin.industrySpecialist" })}</span>
          <span>{vendor?.industrySpecialist?.name || "N/A"}</span>
        </div>
      </div>
      <div className="view-action-button-container">
        <BaseButton text={intl.formatMessage({ id: "usAdmin.edit" })} handleSubmit={onEditClick} />
        <BaseButton text={intl.formatMessage({ id: "usAdmin.delete" })} backgroundColor="red" handleSubmit={handleDelete} />
      </div>
      <DeletionModal
        isOpen={showDeleteModal || deleteVendorSuccess || deleteVendorIsError}
        isError={deleteVendorIsError}
        isSuccess={deleteVendorSuccess}
        loading={deleteVendorLoading}
        deletedEntityName={entityName}
        onClose={() => {
          setShowDeleteModal(false);
          deleteVendorReset();
        }}
        onConfirm={confirmDelete}
      />
    </EntityPageLayout>
  );
};

export default ViewVendorPage;
